import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Button } from '@chakra-ui/react';
import { EmptyState } from '@src/components/chakra-ui/empty-state';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { ProfileLinksEditor } from '@src/components/sco/Advisors/shared/ProfileLinksEditor';
import { advisorBookSessionHref, advisorUserProfileHref, normalizeSessionFeeWaiveCode, suggestSessionFeeWaiveCode } from '@src/components/sco/Advisors/utils/sessionFeeWaiveCode';
import { cleanErrorMessage } from '@src/components/sco/Advisors/utils/sessionFormat';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ROTATE_ADVISOR_SESSION_FEE_WAIVE_CODE } from '@src/gql/mutations/RotateAdvisorSessionFeeWaiveCode';
import { UPSERT_ADVISOR_PROFILE } from '@src/gql/mutations/UpsertAdvisorProfile';
import { ADVISOR_BOOKING_LINK } from '@src/gql/queries/AdvisorBookingLink';
import { MY_ADVISOR_PROFILE } from '@src/gql/queries/MyAdvisorProfile';
import { ScoAdvisorProfile } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useMemo, useState } from 'react';
import { LuUserRound } from 'react-icons/lu';
type ProfileQueryData = {
  myAdvisorProfile?: ScoAdvisorProfile | null;
};
const ProfileLinksForm = ({
  profile
}: {
  profile: ScoAdvisorProfile;
}) => {
  const {
    currentUser
  } = useCurrentUser();
  const slug = currentUser?.slug?.trim() || '';
  const expertUserId = currentUser?.userId || '';
  const [waiveCode, setWaiveCode] = useState(() => normalizeSessionFeeWaiveCode(profile.sessionFeeWaiveCode) || suggestSessionFeeWaiveCode(slug));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [upsert, upsertState] = useMutation(UPSERT_ADVISOR_PROFILE, {
    refetchQueries: [{
      query: MY_ADVISOR_PROFILE
    }]
  });
  const linkQuery = useQuery<ProfileQueryData>(ADVISOR_BOOKING_LINK, {
    fetchPolicy: 'cache-and-network'
  });
  const bookingLinkSessionsCount = linkQuery.data?.myAdvisorProfile?.bookingLinkSessionsCount;
  const [rotate, rotateState] = useMutation<{
    rotateAdvisorSessionFeeWaiveCode?: {
      advisorProfile?: ScoAdvisorProfile | null;
    } | null;
  }>(ROTATE_ADVISOR_SESSION_FEE_WAIVE_CODE);
  const [rotated, setRotated] = useState(false);
  const normalized = normalizeSessionFeeWaiveCode(waiveCode);
  const waiveInvalid = waiveCode.trim() !== '' && !normalized;
  const memberKey = slug || expertUserId;
  const profileUrl = slug ? advisorUserProfileHref(slug) : null;
  const bookUrl = memberKey ? advisorBookSessionHref(memberKey) : '';
  const complimentaryUrl = useMemo(() => memberKey ? advisorBookSessionHref(memberKey, normalized || waiveCode.trim().toLowerCase()) : '', [memberKey, normalized, waiveCode]);
  const handleRotate = async () => {
    setError(null);
    setSaved(false);
    setRotated(false);
    try {
      // Own code only: no userId (admins rotate others' codes from /admin).
      const result = await rotate({
        variables: {
          input: {}
        }
      });
      const next = normalizeSessionFeeWaiveCode(result.data?.rotateAdvisorSessionFeeWaiveCode?.advisorProfile?.sessionFeeWaiveCode);
      if (next) {
        setWaiveCode(next);
      }
      setRotated(true);
    } catch (err) {
      setError(cleanErrorMessage(err, 'Could not rotate the complimentary code.'));
    }
  };
  const handleSave = async () => {
    setError(null);
    setSaved(false);
    setRotated(false);
    if (!normalized) {
      setError('Enter a coupon of 3–32 letters, numbers, or hyphens.');
      return;
    }
    try {
      await upsert({
        variables: {
          input: {
            sessionFeeWaiveCode: normalized
          }
        }
      });
      setWaiveCode(normalized);
      setSaved(true);
    } catch (err_0) {
      setError(cleanErrorMessage(err_0, 'Could not save the complimentary link.'));
    }
  };
  return <ProfileLinksEditor bookingLinkSessionsCount={bookingLinkSessionsCount} bookUrl={bookUrl} complimentaryUrl={complimentaryUrl} error={error} previewUrl={bookUrl || null} profileUrl={profileUrl} rotated={rotated} rotating={rotateState.loading} saved={saved} saving={upsertState.loading} waiveCode={waiveCode} waiveInvalid={waiveInvalid} onRotate={handleRotate} onSave={() => void handleSave()} onWaiveCodeChange={value => {
    setWaiveCode(value);
    setSaved(false);
    setRotated(false);
  }} />;
};
export const ProfileLinksSection = () => {
  const $ = _c(8);
  const paths = useAdvisorsPaths();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      fetchPolicy: "network-only"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const profileQuery = useQuery(MY_ADVISOR_PROFILE, t0);
  const profile = profileQuery.data?.myAdvisorProfile;
  const t1 = profileQuery.loading && !profile;
  let t2;
  if ($[1] !== paths || $[2] !== profile) {
    t2 = profile ? <ProfileLinksForm profile={profile} /> : <EmptyState description="Finish the application, then return here to copy your public links." icon={<LuUserRound />} size="sm" title="Create an advisor profile first"><Button asChild={true} variant="cta"><NextLink href={paths.apply}>Apply to advise</NextLink></Button></EmptyState>;
    $[1] = paths;
    $[2] = profile;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== profileQuery.error || $[5] !== t1 || $[6] !== t2) {
    t3 = <AdvisorsQueryState error={profileQuery.error} loading={t1}>{t2}</AdvisorsQueryState>;
    $[4] = profileQuery.error;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};